import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { formatCents } from "@/lib/format";
import { centsToInput, toCents } from "@/lib/money";
import { useToast } from "@/components/Toaster";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { gatewayName } from "@/lib/payments";
import { isDuplicateReason, paidWith, type PaymentRow } from "./orders";

interface RefundPreview {
  refund?: string;
  to?: string;
  original_payment?: string;
  paid_on?: string;
  full_refund?: boolean;
  access?: string;
  subscription?: string;
  note?: string;
}

/** billing_refund_payment (money): reason required; the server's preview first, then confirm=true.
 *  Same-day payments are voided whole instead of refunded (the gateway hasn't settled them). */
export function RefundDialog({ payment, renewsSubscription, onClose }: { payment: PaymentRow; renewsSubscription?: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const left = payment.amount_cents - payment.refunded_cents;
  const [amount, setAmount] = useState(centsToInput(left));
  const [reason, setReason] = useState("");
  const [cancelSub, setCancelSub] = useState(false);
  const [preview, setPreview] = useState<RefundPreview | null>(null);
  const [problem, setProblem] = useState("");
  const cents = toCents(amount);
  const body = (confirm: boolean) => ({
    amount_cents: cents === left ? undefined : cents,
    reason: reason.trim(),
    cancel_subscription: cancelSub || undefined,
    confirm: confirm || undefined,
  });
  const review = useMutation({
    mutationFn: async () => {
      try {
        await api(`/api/v1/payments/${payment.id}/refund`, { json: body(false) });
        throw new Error("The server didn't ask for a confirmation; nothing was refunded. Tell crm-billing.");
      } catch (e) {
        if (e instanceof ApiError && e.code === "confirm_required") return (e.details ?? {}) as RefundPreview;
        throw e;
      }
    },
    onMutate: () => setProblem(""),
    onSuccess: setPreview,
    onError: (e) => setProblem(e.message),
  });
  const refund = useMutation({
    mutationFn: () => api<{ status: string; method: string; amount_cents: number; message: string }>(`/api/v1/payments/${payment.id}/refund`, { json: body(true) }),
    onSuccess: (r) => {
      for (const k of ["order", "orders", "payments", "billing-summary", "billing-detail", "subscriptions", "subscription"]) void qc.invalidateQueries({ queryKey: [k] });
      toast(r.message || (r.status === "succeeded" ? `${r.method === "void" ? "Voided" : "Refunded"} ${formatCents(r.amount_cents)}` : "The refund didn't go through"), r.status === "succeeded" ? "ok" : "error");
      onClose();
    },
    onError: (e) => setProblem(e.message),
  });
  const amountOk = cents !== null && cents > 0 && cents <= left;
  const full = cents === left;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(560px,calc(100vw-2rem))]">
        <DialogTitle>{preview ? "Check before refunding" : `Refund or void payment #${payment.id}`}</DialogTitle>
        {!preview ? (
          <>
            <DialogDescription>
              {formatCents(payment.amount_cents)} with {paidWith(payment)}
              {payment.refunded_cents ? `, ${formatCents(payment.refunded_cents)} already refunded` : ""}. Money goes back to the card or wallet it came from{payment.gateway ? ` (through ${gatewayName(payment.gateway)})` : ""}. Nothing moves until
              you confirm on the next step.
            </DialogDescription>
            <div className="space-y-3 text-sm">
              <div className="grid gap-3 sm:grid-cols-[10rem_minmax(0,1fr)]">
                <div className="space-y-1">
                  <Label htmlFor="rf-amount">Amount ($)</Label>
                  <Input id="rf-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="rf-reason">Reason (required)</Label>
                  <Input id="rf-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="duplicate charge / asked for a refund by phone" />
                </div>
              </div>
              {amount && !amountOk && <p className="text-xs text-[var(--color-danger-fg)]">Up to {formatCents(left)}.</p>}
              <div className="rounded-md border p-2 text-xs">
                <span className="font-medium">Access rule:</span> a full refund of an order removes the access that order gave (Club, courses…), unless the reason starts with
                "duplicate" (money taken twice; the original order still stands).
                {full && reason.trim() && (
                  <span className={isDuplicateReason(reason) ? "mt-1 block text-[var(--color-success-fg)]" : "mt-1 block text-[var(--color-warning-fg)]"}>
                    {isDuplicateReason(reason) ? "This reason starts with “duplicate”: access stays." : "This full refund will remove the access the order gave."}
                  </span>
                )}
              </div>
              {renewsSubscription && (
                <label className="flex items-start gap-2">
                  <Checkbox className="mt-0.5" checked={cancelSub} onChange={(e) => setCancelSub(e.target.checked)} /> Also cancel the subscription this payment renewed (access ends now)
                </label>
              )}
            </div>
          </>
        ) : (
          <>
            <DialogDescription>This is what {gatewayName(payment.gateway) || "the gateway"} will be asked to do.</DialogDescription>
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
              <dt className="text-[var(--color-muted-foreground)]">Refund</dt>
              <dd className="font-semibold">
                {preview.refund}
                {preview.full_refund ? " (all of it)" : " (part)"}
              </dd>
              <dt className="text-[var(--color-muted-foreground)]">Back to</dt>
              <dd>{preview.to}</dd>
              <dt className="text-[var(--color-muted-foreground)]">Original payment</dt>
              <dd>
                {preview.original_payment}
                {preview.paid_on ? `, ${preview.paid_on}` : ""}
              </dd>
              <dt className="text-[var(--color-muted-foreground)]">Reason</dt>
              <dd>{reason.trim()}</dd>
              {preview.access && (
                <>
                  <dt className="text-[var(--color-muted-foreground)]">Access</dt>
                  <dd className="font-medium">{preview.access}</dd>
                </>
              )}
              {preview.subscription && (
                <>
                  <dt className="text-[var(--color-muted-foreground)]">Subscription</dt>
                  <dd className="font-medium">{preview.subscription}</dd>
                </>
              )}
            </dl>
            {preview.note && <p className="text-xs text-[var(--color-muted-foreground)]">{preview.note}</p>}
          </>
        )}
        {problem && (
          <p role="alert" className="text-sm text-[var(--color-danger-fg)]">
            {problem}
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={preview ? () => setPreview(null) : onClose}>
            {preview ? "Back" : "Cancel"}
          </Button>
          {!preview ? (
            <Button disabled={!amountOk || !reason.trim() || review.isPending} onClick={() => review.mutate()}>
              Review
            </Button>
          ) : (
            <Button variant="destructive" disabled={refund.isPending} onClick={() => refund.mutate()}>
              Refund {preview.refund}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
